/**
 * Convertit un accord (degré, tonalité, mode) en noms de notes pour Tone.js et clavier.
 * Aligné sur le modèle d'accord de l'app (degree, accidental, quality, figure).
 */

const ROOT_NAMES = ['C', 'C#', 'D', 'D#', 'E', 'F', 'F#', 'G', 'G#', 'A', 'A#', 'B']

/** Tonalité (lettre) → pitch class (0-11, C=0) */
const KEY_PC = {
  C: 0, 'C#': 1, Db: 1, D: 2, 'D#': 3, Eb: 3, E: 4, F: 5, 'F#': 6, Gb: 6,
  G: 7, 'G#': 8, Ab: 8, A: 9, 'A#': 10, Bb: 10, B: 11
}

/** Degré romain (I-VII) → indice dans la gamme (0-6) */
const DEGREE_INDEX = { I: 0, II: 1, III: 2, IV: 3, V: 4, VI: 5, VII: 6 }

/** Gamme majeure : intervalles en demi-tons depuis la tonique */
const MAJOR_SCALE = [0, 2, 4, 5, 7, 9, 11]
/** Gamme mineure naturelle */
const MINOR_SCALE = [0, 2, 3, 5, 7, 8, 10]

/**
 * Retourne la pitch class (0-11) de la tonique d'une tonalité.
 * @param {string} tonality - ex. "C", "G", "F"
 * @returns {number}
 */
function keyToPc(tonality) {
  const t = (tonality || 'C').toString().trim()
  return KEY_PC[t] ?? 0
}

/**
 * Retourne les pitch classes des 7 degrés de la gamme.
 * @param {string} tonality
 * @param {'major'|'minor'} mode
 * @returns {number[]} 7 pitch classes
 */
function getScalePcs(tonality, mode = 'major') {
  const root = keyToPc(tonality)
  const scale = mode === 'minor' ? MINOR_SCALE : MAJOR_SCALE
  return scale.map((interval) => (root + interval) % 12)
}

/**
 * Convertit une pitch class (0-11) en nom de note avec octave.
 * @param {number} pc
 * @param {number} octave
 * @returns {string} ex. "C4", "F#4"
 */
function pcToNoteName(pc, octave = 4) {
  const name = ROOT_NAMES[((pc % 12) + 12) % 12]
  return `${name}${octave}`
}

/**
 * Retourne les noms de notes (ex. ['C4','E4','G4']) pour un accord donné.
 * @param {Object} chord - { degree, accidental?, quality?, figure? }
 *   degree: I-VII, accidental: 'b'|'#', quality: 'm'|'°'|'+', figure: '5'|'6'|'64'|'7'|'65'|'43'|'2'
 * @param {string} tonality - ex. "C", "G"
 * @param {'major'|'minor'} mode
 * @param {number} octave - octave de la fondamentale (défaut 4)
 * @returns {string[]} note names
 */
export function chordToNotes(chord, tonality = 'C', mode = 'major', octave = 4) {
  if (!chord || !chord.degree) return []
  const scalePcs = getScalePcs(tonality, mode)
  const deg = (chord.degree || 'I').toString().trim().toUpperCase()
  const index = DEGREE_INDEX[deg]
  if (index === undefined) return []
  let rootPc = scalePcs[index]
  if (chord.accidental === 'b') rootPc = (rootPc - 1 + 12) % 12
  if (chord.accidental === '#') rootPc = (rootPc + 1) % 12
  if (chord.specialRoot === 'N') rootPc = (rootPc - 1 + 12) % 12 // IIb

  const quality = (chord.quality || '').toString().trim()
  const isDim = quality === '°' || quality === 'dim' || quality === 'ø'
  const thirdInterval = isDim ? 3 : quality === '+' ? 4 : quality === 'm' ? 3 : 4
  const fifthInterval = isDim ? 6 : 7
  const seventhInterval = 10 // septième mineure (dominante) ; ° ou ø = demi-diminué (7te mineure)

  const root = rootPc
  const third = (rootPc + thirdInterval) % 12
  const fifth = (rootPc + fifthInterval) % 12
  const seventh = (rootPc + seventhInterval) % 12

  let notes = []
  const fig = (chord.figure || '5').toString().trim()
  if (fig === '5' || fig === '') {
    notes = [root, third, fifth]
  } else if (fig === '6') {
    notes = [third, fifth, root + 12]
  } else if (fig === '64') {
    notes = [fifth, root + 12, third + 12]
  } else if (fig === '7') {
    notes = [root, third, fifth, seventh]
  } else if (fig === '65') {
    notes = [third, fifth, seventh, root + 12]
  } else if (fig === '43') {
    notes = [fifth, seventh, root + 12, third + 12]
  } else if (fig === '2') {
    notes = [seventh, root + 12, third + 12, fifth + 12]
  } else {
    notes = [root, third, fifth]
  }

  const result = []
  const used = new Set()
  let prevMidi = -1
  for (let i = 0; i < notes.length; i++) {
    let midi = (notes[i] % 12) + (octave + Math.floor(notes[i] / 12)) * 12
    while (midi <= prevMidi) midi += 12
    prevMidi = midi
    const o = Math.min(6, Math.max(2, Math.floor(midi / 12)))
    const pc = midi % 12
    const name = pcToNoteName(pc, o)
    if (!used.has(name)) {
      used.add(name)
      result.push(name)
    }
  }
  return result.sort((a, b) => noteToMidi(a) - noteToMidi(b))
}

/**
 * Retourne la tonalité et le mode effectifs pour un accord avec ofDegree/borrowedFromDegree.
 * Ex. en Do majeur, ofDegree 'VI' → La mineur ; ofDegree 'V' → Sol majeur.
 * @param {string} tonality - ex. "C", "G"
 * @param {'major'|'minor'} mode
 * @param {string} ofDegree - degré cible (I-VII)
 * @returns {{ tonality: string, mode: string }}
 */
function getEffectiveKey(tonality, mode, ofDegree) {
  const scalePcs = getScalePcs(tonality, mode)
  const deg = (ofDegree || '').toString().trim().toUpperCase()
  const index = DEGREE_INDEX[deg]
  if (index === undefined) return { tonality: tonality || 'C', mode: mode || 'major' }
  const rootPc = scalePcs[index]
  const effectiveTonality = ROOT_NAMES[((rootPc % 12) + 12) % 12]
  const relativeMinorInMajor = mode === 'major' && (deg === 'VI' || deg === 'III')
  const relativeMajorInMinor = mode === 'minor' && (deg === 'III' || deg === 'VI')
  const effectiveMode = relativeMinorInMajor ? 'minor' : relativeMajorInMinor ? 'major' : mode
  return { tonality: effectiveTonality, mode: effectiveMode }
}

/**
 * Retourne les pitch classes (0-11) et la basse pour un accord, pour comparaison d'équivalence.
 * Gère pedalDegree (basse = degré de pédale, note incluse dans l'ensemble) et ofDegree/borrowedFromDegree (calcul dans la tonalité cible).
 * @param {Object} chord - { degree, accidental?, quality?, figure?, pedalDegree?, ofDegree?, borrowedFromDegree? }
 * @param {string} tonality - ex. "C", "G"
 * @param {'major'|'minor'} mode
 * @returns {{ bassPc: number, setPc: Set<number> } | null} - null si accord invalide
 */
export function chordToPitchSet(chord, tonality = 'C', mode = 'major') {
  if (!chord || !chord.degree) return null
  const of = (chord.ofDegree || chord.borrowedFromDegree || '').toString().trim().toUpperCase()
  const { tonality: effT, mode: effM } = of
    ? getEffectiveKey(tonality, mode, of)
    : { tonality: tonality || 'C', mode: mode || 'major' }
  const scalePcs = getScalePcs(effT, effM)
  const deg = (chord.degree || 'I').toString().trim().toUpperCase()
  const index = DEGREE_INDEX[deg]
  if (index === undefined) return null
  let rootPc = scalePcs[index]
  if (chord.accidental === 'b') rootPc = (rootPc - 1 + 12) % 12
  if (chord.accidental === '#') rootPc = (rootPc + 1) % 12
  if (chord.specialRoot === 'N') rootPc = (rootPc - 1 + 12) % 12

  const quality = (chord.quality || '').toString().trim()
  const isDim = quality === '°' || quality === 'dim' || quality === 'ø'
  const thirdInterval = isDim ? 3 : quality === '+' ? 4 : quality === 'm' ? 3 : 4
  const fifthInterval = isDim ? 6 : 7
  const seventhInterval = 10
  const root = rootPc
  const third = (rootPc + thirdInterval) % 12
  const fifth = (rootPc + fifthInterval) % 12
  const seventh = (rootPc + seventhInterval) % 12

  const fig = (chord.figure || '5').toString().trim()
  let bassPc
  const pcs = []
  if (fig === '5' || fig === '') {
    pcs.push(root, third, fifth)
    bassPc = root
  } else if (fig === '6') {
    pcs.push(third, fifth, root)
    bassPc = third
  } else if (fig === '64') {
    pcs.push(fifth, root, third)
    bassPc = fifth
  } else if (fig === '7') {
    pcs.push(root, third, fifth, seventh)
    bassPc = root
  } else if (fig === '65') {
    pcs.push(third, fifth, seventh, root)
    bassPc = third
  } else if (fig === '43') {
    pcs.push(fifth, seventh, root, third)
    bassPc = fifth
  } else if (fig === '2') {
    pcs.push(seventh, root, third, fifth)
    bassPc = seventh
  } else {
    pcs.push(root, third, fifth)
    bassPc = root
  }

  const setPc = new Set(pcs.map((pc) => (pc % 12 + 12) % 12))

  if (chord.pedalDegree) {
    const mainScale = getScalePcs(tonality, mode)
    const pedalDeg = (chord.pedalDegree || '').toString().trim().toUpperCase()
    const pedalIndex = DEGREE_INDEX[pedalDeg]
    if (pedalIndex !== undefined) {
      const pedalPc = (mainScale[pedalIndex] % 12 + 12) % 12
      setPc.add(pedalPc)
      bassPc = pedalPc
    }
  }

  return { bassPc: (bassPc % 12 + 12) % 12, setPc }
}

const ROMAN_BY_INDEX = ['I', 'II', 'III', 'IV', 'V', 'VI', 'VII']

/**
 * Mappe une pitch class de basse au degré romain diatonique dans la tonalité principale (I–VII), ou null si hors gamme.
 * @param {number} bassPc
 * @param {string} tonality
 * @param {'major'|'minor'} mode
 * @returns {'I'|'II'|'III'|'IV'|'V'|'VI'|'VII'|null}
 */
export function pitchClassToRomanDegreeInKey(bassPc, tonality = 'C', mode = 'major') {
  const scalePcs = getScalePcs(tonality, mode)
  const pc = ((bassPc % 12) + 12) % 12
  for (let i = 0; i < 7; i++) {
    if (((scalePcs[i] % 12) + 12) % 12 === pc) return ROMAN_BY_INDEX[i]
  }
  return null
}

/**
 * Degré de basse en tonalité principale (dominantes secondaires, emprunts, renversements) pour la continuité de pédale.
 * Repli possible : null si accord invalide ou basse non diatonique en tonalité principale.
 * @param {Object} chord
 * @param {string} tonality
 * @param {'major'|'minor'} mode
 * @returns {'I'|'II'|'III'|'IV'|'V'|'VI'|'VII'|null}
 */
export function getBassRomanDegreeInMainKey(chord, tonality = 'C', mode = 'major') {
  if (!chord || !chord.degree) return null
  if (chord.pedalBlocked !== true && chord.pedalDegree != null && String(chord.pedalDegree).trim() !== '') {
    return String(chord.pedalDegree).trim().toUpperCase()
  }
  const pitch = chordToPitchSet(chord, tonality, mode)
  if (!pitch) return null
  return pitchClassToRomanDegreeInKey(pitch.bassPc, tonality, mode)
}

/**
 * Retourne un ordre MIDI approximatif pour trier les notes (nom → nombre).
 * @param {string} name - ex. "C4"
 * @returns {number}
 */
function noteToMidi(name) {
  const m = name.match(/^([A-G]#?b?)(\d+)$/i)
  if (!m) return 60
  const pc = KEY_PC[m[1]] ?? 0
  const oct = parseInt(m[2], 10)
  return oct * 12 + pc
}

/**
 * Retourne toutes les notes d'une progression (séquence d'accords).
 * @param {Object[]} chords - tableau d'objets accord
 * @param {string} tonality
 * @param {'major'|'minor'} mode
 * @param {number} octave
 * @returns {string[]} toutes les notes uniques (pour surlignage clavier)
 */
export function progressionToNotes(chords, tonality = 'C', mode = 'major', octave = 4) {
  if (!Array.isArray(chords) || chords.length === 0) return []
  const set = new Set()
  for (const ch of chords) {
    chordToNotes(ch, tonality, mode, octave).forEach((n) => set.add(n))
  }
  return Array.from(set).sort((a, b) => noteToMidi(a) - noteToMidi(b))
}

/**
 * Retourne une séquence pour Tone.js : [ [ time, [notes] ], ... ] (une entrée par accord).
 * @param {Object[]} chords
 * @param {string} tonality
 * @param {'major'|'minor'} mode
 * @param {number} octave
 * @param {string} duration - ex. "2n", "1n"
 * @returns {Array<[string, string[]]>} [ [ "0:0:0", ["C4","E4","G4"] ], [ "0:2:0", ... ] ]
 */
export function progressionToToneSequence(chords, tonality = 'C', mode = 'major', octave = 4, duration = '2n') {
  if (!Array.isArray(chords) || chords.length === 0) return []
  return chords.map((chord, i) => {
    const time = `0:${i * 2}:0`
    const notes = chordToNotes(chord, tonality, mode, octave)
    return [time, notes]
  })
}
